import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import EmptyState from '@/components/common/EmptyState';
import PermissionDialog from './permissions/PermissionDialog';
import PermissionTable from './permissions/PermissionTable';
import { usePermissionManagement } from './permissions/usePermissionManagement';

interface PermissionManagementViewProps {
  documentId?: string;
}

const PermissionManagementView: React.FC<PermissionManagementViewProps> = ({ documentId }) => {
  const { t } = useTranslation();
  const {
    permissions,
    availableEntities,
    loading,
    error,
    actionError,
    dialogError,
    saving,
    addDialogOpen,
    editingPermission,
    selectedEntity,
    permissionRights,
    setSelectedEntity,
    toggleRight,
    openAddDialog,
    openEditDialog,
    deletePermission,
    savePermission,
    closeDialog,
  } = usePermissionManagement(documentId);

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription className="revy-cjk text-sm leading-[1.5]">{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div>
      {actionError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="revy-cjk text-sm leading-[1.5]">{actionError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
          {t('permissions.manageDescription')}
        </p>
        <Button onClick={openAddDialog}>
          <Plus strokeWidth={1.8} />
          <span className="revy-cjk">{t('permissions.addPermission')}</span>
        </Button>
      </div>

      {permissions.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title={t('permissions.noPermissions')}
          description={t('permissions.noPermissionsDescription')}
        />
      ) : (
        <PermissionTable
          permissions={permissions}
          onEdit={openEditDialog}
          onDelete={deletePermission}
        />
      )}

      <PermissionDialog
        open={addDialogOpen}
        isEditing={Boolean(editingPermission)}
        entities={availableEntities}
        selectedEntity={selectedEntity}
        onSelectedEntityChange={setSelectedEntity}
        rights={permissionRights}
        onToggleRight={toggleRight}
        onSave={() => {
          void savePermission();
        }}
        onClose={closeDialog}
        saving={saving}
        error={dialogError}
      />
    </div>
  );
};

export default PermissionManagementView;
